import { Box, Link, Stack, Typography, alpha } from "@mui/material";
import type { DocumentLoadStatus, TargetSchema } from "@/types";
import { ENTITY_CONFIG, STATUS_CONFIG } from "./config";

interface ResultStatusBannerProps {
  status: DocumentLoadStatus;
  loadedCount: number;
  failedCount: number;
  /** Target schemas present in the latest load. Each becomes an anchor link
   *  that scrolls to its `SchemaResultCard`. */
  schemas: TargetSchema[];
  /** Builds the DOM id for the schema's card so the banner links can scroll
   *  to it. Owned by the caller to avoid coupling. */
  schemaAnchorId: (schema: TargetSchema) => string;
}

/**
 * Top-of-tab status bar for the Result tab.
 *
 * Slim single-row design — colored status icon + status label + record
 * counts on the left, schema anchor links on the right. Status color
 * (success / warning / error) drives a left-border accent and a subtle
 * background tint. The row wraps on narrow widths so schema links fall
 * below when there's not enough horizontal room.
 */
export const ResultStatusBanner = ({
  status,
  loadedCount,
  failedCount,
  schemas,
  schemaAnchorId,
}: ResultStatusBannerProps) => {
  const config = STATUS_CONFIG[status];
  const Icon = config.icon;
  // Map the STATUS_CONFIG color to the MUI palette key so the bar's accent
  // and tint stay in sync with the icon color used elsewhere on the page.
  const paletteKey = config.color; // "success" | "warning" | "error"

  const recordsLine = `${loadedCount} record${loadedCount !== 1 ? "s" : ""} loaded${
    failedCount > 0 ? `, ${failedCount} failed` : ""
  }`;

  const scrollToAnchor = (id: string) => () => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 1.5,
        px: 1.5,
        py: 1,
        mb: 2,
        border: 1,
        borderColor: (theme) => alpha(theme.palette[paletteKey].main, 0.4),
        borderLeft: 4,
        borderLeftColor: `${paletteKey}.main`,
        borderRadius: 1,
        bgcolor: (theme) => alpha(theme.palette[paletteKey].main, theme.palette.mode === "dark" ? 0.1 : 0.06),
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
        <Icon sx={{ fontSize: 18, color: `${paletteKey}.main`, flexShrink: 0 }} />
        <Typography variant="body2" fontWeight={600} sx={{ flexShrink: 0 }}>
          {config.label}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
          · {recordsLine}
        </Typography>
      </Stack>
      {schemas.length > 0 && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 600 }}
          >
            Jump to
          </Typography>
          {schemas.map((schema, i) => (
            <Box key={schema} sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
              {i > 0 && (
                <Box component="span" sx={{ color: "text.disabled" }} aria-hidden="true">
                  ·
                </Box>
              )}
              <Link
                component="button"
                type="button"
                onClick={scrollToAnchor(schemaAnchorId(schema))}
                underline="hover"
                sx={{ fontSize: "0.8125rem", fontWeight: 500, cursor: "pointer" }}
              >
                {ENTITY_CONFIG[schema].labelPlural}
              </Link>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
};
